import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  blueprints,
  computeBuildStats,
  experimentalEffects,
  modulesBySymbol,
  modulesForSlot,
  shipsBySlug,
  type BuildFit,
  type FitModule,
  type ModuleDef,
  type ShipBuildSlot,
} from "@edcc/game-data";
import { engineerEffectIconFor } from "../lib/engineer-effect-icon.ts";
import {
  categoryForCommunityBuildRole,
  COMMUNITY_BUILD_CATEGORIES,
  shipSizeLabel,
  type CommunityBuildRole,
} from "../lib/community-builds.ts";
import { moduleArtFor } from "../lib/module-art.ts";
import { moduleFacts, moduleStatFacts } from "../lib/module-details.ts";
import { shipArtFor } from "../lib/ship-art.ts";
import { ModulePropertyMarkers } from "../../../web/src/components/ModulePropertyMarkers.tsx";

import { ShipFitWorkbench } from "../../../web/src/components/ShipFitWorkbench.tsx";
import { BuildAvailabilityPanel } from "../../../web/src/components/BuildAvailabilityPanel.tsx";

const API = "/v1";
const groupDefinitions = [
  { key: "core", label: "Core Internals", icon: "/ed-icons/EDO-Icon-Equipment-Core_Internal.png" },
  { key: "optional", label: "Optional Internals", icon: "/ed-icons/EDO-Icon-Equipment-Optional_Internals.png" },
  { key: "hardpoint", label: "Hardpoints", icon: "/ed-icons/EDO-Icon-Equipment-Hardpoints.png" },
  { key: "utility", label: "Utility Mounts", icon: "/ed-icons/EDO-Icon-Equipment-Utility_Mounts.png" },
] as const;

type SharedBuildModule = FitModule;

interface SharedBuild {
  id: string;
  createdAt: string;
  name: string;
  shipSlug: string;
  shipName: string;
  modules: SharedBuildModule[];
  community?: {
    role: CommunityBuildRole;
    imageUrl?: string;
    galleryImages?: string[];
    videoUrl?: string;
    creatorName?: string;
    creatorUrl?: string;
    groupName?: string;
    firegroups?: string;
    requirements?: string;
    limitations?: string;
    edsyUrl?: string;
    coriolisUrl?: string;
    description?: string;
    stats?: string[];
  };
}

interface CommunityBuildEntry {
  id: string;
  buildId: string;
  role: CommunityBuildRole;
  notes: string;
  submittedAt: string;
  sourceUrl?: string;
  imageUrl?: string;
  galleryImages?: string[];
  videoUrl?: string;
  edsyUrl?: string;
  coriolisUrl?: string;
  description?: string;
  stats?: string[];
  groupName?: string;
  firegroups?: string;
  requirements?: string;
  limitations?: string;
  build: SharedBuild;
}

type BuildMediaItem =
  | { kind: "image"; src: string; label: string }
  | { kind: "video"; src: string; label: string };

function slotLabel(slot: ShipBuildSlot): string {
  if (slot.label) return slot.label;
  const optional = /^Slot(\d+)_Size(\d+)$/.exec(slot.slotName);
  if (optional) return `Optional ${Number(optional[1])} · Class ${optional[2]}`;
  const hardpoint = /^Hardpoint(\d+)$/.exec(slot.slotName);
  if (hardpoint) return `Hardpoint ${Number(hardpoint[1])}${slot.size ? ` · Class ${slot.size}` : ""}`;
  const utility = /^TinyHardpoint(\d+)$/.exec(slot.slotName);
  if (utility) return `Utility ${Number(utility[1])}`;
  const labels: Record<string, string> = {
    PowerPlant: "Power Plant", MainEngines: "Thrusters", FrameShiftDrive: "Frame Shift Drive",
    LifeSupport: "Life Support", PowerDistributor: "Power Distributor", Radar: "Sensors",
    FuelTank: "Fuel Tank", Armour: "Bulkhead", CargoHatch: "Cargo Hatch",
  };
  return labels[slot.slotName] ?? slot.slotName.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
}

function ptnBuildImageSrc(value: string | undefined): string | null {
  if (!value || value.length > 2048) return null;
  try {
    const url = new URL(value);
    if (
      url.protocol !== "https:"
      || url.hostname !== "pilotstradenetwork.com"
      || url.username
      || url.password
      || url.port
      || !url.pathname.startsWith("/wp-content/uploads/")
      || !/\.(?:png|jpe?g|webp)$/i.test(url.pathname)
    ) return null;
    return `/ptn-build-image?url=${encodeURIComponent(url.href)}`;
  } catch {
    return null;
  }
}

function communityImageSrc(value: string): string | null {
  const ptnImage = ptnBuildImageSrc(value);
  if (ptnImage) return ptnImage;
  try {
    const url = new URL(value, window.location.origin);
    if (/^\/v1\/community-builds\/media\/[a-f0-9]{32}\.(?:png|jpg|webp)$/.test(url.pathname)) {
      return url.origin === window.location.origin ? url.href : null;
    }
    if (url.protocol !== "https:" || url.username || url.password || url.port || url.href.length > 2048) return null;
    return url.href;
  } catch {
    return null;
  }
}

function youtubeEmbedSrc(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.username || url.password || url.port) return null;
    const videoId = url.hostname === "youtu.be"
      ? /^\/([A-Za-z0-9_-]{11})$/.exec(url.pathname)?.[1]
      : ["youtube.com", "www.youtube.com"].includes(url.hostname)
        ? url.pathname === "/watch"
          ? /^[A-Za-z0-9_-]{11}$/.test(url.searchParams.get("v") ?? "") ? url.searchParams.get("v") : null
          : /^\/embed\/([A-Za-z0-9_-]{11})$/.exec(url.pathname)?.[1]
        : null;
    return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}?rel=0` : null;
  } catch {
    return null;
  }
}

function communityDescription(text: string) {
  const paragraphs = text.split(/\n{2,}/).map((paragraph) => paragraph.trim()).filter(Boolean);
  return paragraphs.map((paragraph, index) => {
    const section = /^(Overview|Strategy|Outfitting|Engineering|Alternative fit|Requirements?|Note):\s*([\s\S]*)$/i.exec(paragraph);
    const content = section?.[2] ?? paragraph;
    const parts = content.split(/(\[[^\]]+\]\(https:\/\/elite-dangerous\.fandom\.com\/wiki\/Empire\/Ranks\))/g);
    return <p key={`${index}-${paragraph.slice(0, 24)}`}>
      {section ? <strong>{section[1]}: </strong> : null}
      {parts.map((part, partIndex) => {
        const link = /^\[([^\]]+)\]\((https:\/\/elite-dangerous\.fandom\.com\/wiki\/Empire\/Ranks)\)$/.exec(part);
        return link
          ? <a key={partIndex} href={link[2]} target="_blank" rel="noreferrer">{link[1]}</a>
          : part;
      })}
    </p>;
  });
}

function communityBuildSection(entry: CommunityBuildEntry): string {
  const supplied = entry.groupName?.trim();
  if (supplied && !/^(?:ship builds|builds|home)$/i.test(supplied)) return supplied;

  const text = `${entry.build.name} ${entry.description ?? ""} ${(entry.stats ?? []).join(" ")}`.toLowerCase();
  const engineered = entry.build.modules.some((module) => {
    const engineering = module.engineering;
    return Boolean(engineering?.blueprintName || engineering?.blueprintFdName || engineering?.grade || engineering?.experimentalEffect);
  });

  if (entry.role === "mining") {
    if (/multi[- ]?miner|multi mining/.test(text)) return "Multi miners";
    if (/core miner|seismic|sub[- ]?surface|abrasion/.test(text)) return "Core miners";
    if (/laser miner|laser mining/.test(text)) return "Laser miners";
  }
  if (entry.role === "hauling" || entry.role === "trading") {
    const ship = shipsBySlug.get(entry.build.shipSlug);
    if (ship?.class === 3) return "Large-pad builds";
    if (ship?.class === 2) return "Medium-pad builds";
    if (ship?.class === 1) return "Small-pad builds";
  }
  if (entry.role === "combat") {
    if (/assassination/.test(text)) return "Assassination builds";
    if (/\bpvp\b|player versus player/.test(text)) return "PvP builds";
    if (/res\/?cz|resource extraction|conflict zone/.test(text)) return engineered ? "Engineered builds: RES/CZ" : "Beginner builds: no engineering";
    if (/no engineering|unengineered|beginner/.test(text)) return "Beginner builds: no engineering";
  }
  if (entry.role === "exploration") {
    if (/starter|beginner/.test(text)) return "Starter explorers";
    if (/long[- ]?range|jump range|ly\b/.test(text)) return "Long-range explorers";
  }
  return engineered ? "Engineered builds" : "Unengineered builds";
}

function communityGroupSummary(role: CommunityBuildRole, label: string): string {
  const group = label.toLowerCase();
  if (group.includes("no engineering") || group.includes("unengineered") || group.includes("beginner")) {
    return "Starter configurations with no engineered modules. Stats, notes, and fit links are shown when available.";
  }
  if (group.includes("engineer")) {
    return "Modified loadouts grouped by their intended role. Review the build notes and available fit details.";
  }
  if (role === "mining") {
    if (group.includes("laser")) return "Laser mining loadouts for collecting surface deposits from asteroids.";
    if (group.includes("core")) return "Core mining loadouts for locating and cracking asteroid cores.";
    if (group.includes("multi")) return "Multi-role mining loadouts combining more than one mining method.";
  }
  if (role === "exploration") return "Exploration configurations, with recorded range and equipment details where available.";
  if (role === "combat" && group.includes("pvp")) return "Ship fits intended for player-versus-player combat.";
  return "Community ship fits with available images, stats, recommendations, and loadout links.";
}

function BuildStatIcon({ stat }: { stat: string }) {
  const value = stat.toLowerCase();
  if (/\b(?:small|medium|large|ship size)\b/.test(value)) {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m3 15 2-5 5-2 2-3h4l3 4 2 1v5H3Z" /><circle cx="7" cy="17" r="2" /><circle cx="18" cy="17" r="2" /></svg>;
  }
  if (/\b(?:cr|credits?)\b/.test(value)) {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" /><path d="M15 9.5c-.6-.7-1.5-1-2.7-1-1.3 0-2.2.6-2.2 1.5 0 2.4 5.8.8 5.8 3.9 0 1.2-1.2 2-2.8 2-1.2 0-2.2-.4-3-1.2M12 6.7v10.6" /></svg>;
  }
  if (/engineer|grade|modified/.test(value)) {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.4 6.1a4.5 4.5 0 0 0-5.8 5.8L3.5 17l3.5 3.5 5.1-5.1a4.5 4.5 0 0 0 5.8-5.8l-2.8 2.8-3.1-3.1z" /></svg>;
  }
  if (/boost|speed|m\/s/.test(value)) {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4.5 18a8 8 0 1 1 15 0" /><path d="m12 13 4-4M7.5 18h9" /><circle cx="12" cy="13" r="1" /></svg>;
  }
  if (/\bly\b|jump|range/.test(value)) {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="6" cy="17" r="2" /><circle cx="18" cy="7" r="2" /><path d="M8 16c3.5 0 2-8 7-8M12 6h3v3" /></svg>;
  }
  return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" /></svg>;
}

interface BuildSpecSummary {
  stats: string[];
  note: string | null;
}

function formatCredits(value: number): string {
  if (value >= 1_000_000_000) return `${Number((value / 1_000_000_000).toFixed(1))}b Cr`;
  if (value >= 1_000_000) return `${Number((value / 1_000_000).toFixed(1))}m Cr`;
  if (value >= 1_000) return `${Number((value / 1_000).toFixed(1))}k Cr`;
  return `${Math.round(value).toLocaleString("en-US")} Cr`;
}

function formatMetric(value: number, digits: number): string {
  return Number(value.toFixed(digits)).toString();
}

function formatMetricRange(
  minimum: number | null,
  maximum: number | null,
  digits: number,
  suffix: string,
): string | null {
  if (minimum === null || maximum === null) return null;
  const lower = Math.min(minimum, maximum);
  const upper = Math.max(minimum, maximum);
  const low = formatMetric(lower, digits);
  const high = formatMetric(upper, digits);
  return Math.abs(upper - lower) < 10 ** -digits
    ? `${high}${suffix}`
    : `${low}-${high}${suffix}`;
}

function buildSpecSummary(build: SharedBuild, suppliedStats: string[] = []): BuildSpecSummary {
  const ship = shipsBySlug.get(build.shipSlug);
  const stats = suppliedStats.map((stat) => stat.trim()).filter(Boolean);
  if (!ship) return { stats, note: null };

  let completePriceData = ship.retailCost !== null;
  const fits: BuildFit[] = [];
  for (const saved of build.modules) {
    const slot = ship.buildSlots.find((candidate) => candidate.slotName === saved.slotName);
    if (!slot) {
      completePriceData = false;
      continue;
    }

    const module = saved.moduleSymbol
      ? modulesBySymbol.get(saved.moduleSymbol.toLowerCase()) ?? null
      : null;
    const bulkhead = slot.kind === "bulkhead"
      ? ship.bulkheads.find((candidate) =>
        candidate.symbol?.toLowerCase() === saved.moduleSymbol?.toLowerCase()
        || candidate.name.toLowerCase() === saved.moduleName?.toLowerCase()
      ) ?? null
      : null;
    if (slot.kind === "bulkhead") {
      if (!bulkhead || bulkhead.cost === null) completePriceData = false;
    } else if (!module || (module.purchaseCurrency !== "merc-coin" && module.cost === null)) {
      completePriceData = false;
    }

    fits.push({
      slot,
      module: slot.kind === "bulkhead" ? null : module,
      bulkhead,
      ...(saved.engineering && (saved.engineering.blueprintFdName || saved.engineering.blueprintName)
        ? { engineering: saved.engineering }
        : {}),
    });
  }

  const minimum = computeBuildStats(ship, fits, { engineeringRoll: "minimum" });
  const maximum = computeBuildStats(ship, fits, { engineeringRoll: "maximum" });
  let calculated = false;
  const addCalculated = (pattern: RegExp, value: string | null) => {
    if (value && !stats.some((stat) => pattern.test(stat))) {
      stats.push(value);
      calculated = true;
    }
  };

  addCalculated(/\b(?:cr|credits?)\b/i, completePriceData ? formatCredits(maximum.price) : null);
  const grades = build.modules
    .map((module) => module.engineering?.grade)
    .filter((grade): grade is number => grade !== null && grade !== undefined && grade > 0);
  const engineeringLabel = grades.length
    ? `Engineered · G${Math.max(...grades)} max`
    : "Unengineered";
  addCalculated(/engineer|grade\s*\d|modified/i, engineeringLabel);
  addCalculated(/boost/i, formatMetricRange(minimum.boost, maximum.boost, 1, " m/s boost"));
  addCalculated(/top speed|speed/i, formatMetricRange(minimum.speed, maximum.speed, 1, " m/s top speed"));
  addCalculated(/\bly\b|jump|range/i, formatMetricRange(minimum.jumpUnladen, maximum.jumpUnladen, 2, " ly unladen"));

  const hasExperimentalEffects = build.modules.some((module) => module.engineering?.experimentalEffect);
  const incompleteEngineering = maximum.warnings.some((warning) =>
    warning.startsWith("Some engineering modifiers could not be applied")
  );
  const note = calculated
    ? `${grades.length ? "EDCC estimate using published blueprint bounds" : "EDCC estimate from published ship/module data"}${hasExperimentalEffects ? "; experimental effects excluded" : ""}${incompleteEngineering ? "; some modifiers unavailable" : ""}.`
    : null;

  return { stats, note };
}

function ShareLinkIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="18" cy="5" r="2.5" /><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="19" r="2.5" /><path d="m8.3 10.8 7.4-4.5m-7.4 7 7.4 4.5" /></svg>;
}

function sharedModuleForSlot(build: SharedBuild, slot: ShipBuildSlot): SharedBuildModule | undefined {
  return build.modules.find((module) => module.slotName === slot.slotName);
}

function moduleFromDefinition(slot: ShipBuildSlot, module: ModuleDef): SharedBuildModule {
  return {
    slotName: slot.slotName,
    slotKind: slot.kind,
    slotIndex: slot.index,
    moduleName: module.name,
    moduleSymbol: module.symbol,
    size: module.class,
    engineering: null,
  };
}

function engineeringKey(effectName: string | null): string | null {
  if (!effectName) return null;
  return experimentalEffects.find((effect) => effect.name === effectName)?.key ?? null;
}

function buildCode(build: SharedBuild): string {
  return JSON.stringify({
    format: "edcc-build",
    version: 1,
    ship: build.shipSlug,
    name: build.name,
    modules: build.modules.map((module) => ({
      slot: module.slotName,
      kind: module.slotKind,
      index: module.slotIndex,
      ...(module.moduleSymbol ? { symbol: module.moduleSymbol } : {}),
      ...(module.moduleName ? { name: module.moduleName } : {}),
      ...(module.size != null ? { size: module.size } : {}),
      ...(module.engineering?.blueprintFdName ? { blueprint: module.engineering.blueprintFdName } : {}),
      ...(module.engineering?.grade ? { grade: module.engineering.grade } : {}),
      ...(engineeringKey(module.engineering?.experimentalEffect ?? null)
        ? { experimental: engineeringKey(module.engineering?.experimentalEffect ?? null) }
        : {}),
    })),
  });
}

export function PublicBuildPage({ id }: { id: string }) {
  const [build, setBuild] = useState<SharedBuild | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing" | "offline">("loading");
  const [manageToken] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("edit") ?? "");
  const [editing, setEditing] = useState(false);
  const [buildName, setBuildName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editStatus, setEditStatus] = useState("");
  const [editError, setEditError] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/builds/${encodeURIComponent(id)}`)
      .then(async (response) => {
        if (!response.ok) throw new Error(response.status === 404 ? "missing" : "offline");
        return response.json() as Promise<SharedBuild>;
      })
      .then((result) => {
        if (!cancelled) {
          setBuild(result);
          setBuildName(result.name);
          setState("ready");
        }
      })
      .catch((error: unknown) => { if (!cancelled) setState(error instanceof Error && error.message === "missing" ? "missing" : "offline"); });
    return () => { cancelled = true; };
  }, [id]);

  const ship = build ? shipsBySlug.get(build.shipSlug) : undefined;
  const groups = useMemo(() => ship ? groupDefinitions.map((group) => ({
    ...group,
    slots: ship.buildSlots
      .filter((slot) => (slot.kind === "bulkhead" ? "core" : slot.kind) === group.key)
      .sort((a, b) => a.index - b.index),
  })).filter((group) => group.slots.length > 0) : [], [ship]);
  const saveRevision = async () => {
    if (!build || !manageToken) return;
    setSaving(true);
    setEditError("");
    setEditStatus("Saving the revised build and creating its new link…");
    try {
      const response = await fetch(`${API}/builds/${encodeURIComponent(build.id)}/revise`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ deleteToken: manageToken, code: buildCode({ ...build, name: buildName.trim() || build.name }) }),
      });
      const result = await response.json() as { id?: string; deleteToken?: string; error?: { message?: string } };
      if (!response.ok || !result.id || !result.deleteToken) throw new Error(result.error?.message ?? `Could not revise this build (HTTP ${response.status}).`);
      window.location.assign(`/build/${result.id}#edit=${encodeURIComponent(result.deleteToken)}`);
    } catch (error) {
      setEditError(error instanceof Error ? error.message : "Could not revise this build. Retry when the service is available.");
      setEditStatus("");
    } finally {
      setSaving(false);
    }
  };

  const removePublishedBuild = async () => {
    if (!build || !manageToken || !window.confirm(`Remove ${build.name} from the website? This cannot be undone.`)) return;
    setSaving(true); setEditError("");
    try {
      const response = await fetch(`${API}/builds/${encodeURIComponent(build.id)}`, { method: "DELETE", headers: { "content-type": "application/json" }, body: JSON.stringify({ deleteToken: manageToken }) });
      if (!response.ok) throw new Error(`Could not remove the build (HTTP ${response.status}).`);
      window.location.assign("/builds/");
    } catch (error) { setEditError(error instanceof Error ? error.message : "Could not remove the build."); }
    finally { setSaving(false); }
  };

  const copyBuildLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyStatus("Build link copied.");
    } catch {
      setCopyStatus("Could not copy the build link. Copy it from your browser’s address bar.");
    }
  };

  if (state !== "ready" || !build) return <section className="ud-section public-build-page"><div className="container simple-page">
    <h1>{state === "loading" ? "Loading the build…" : state === "missing" ? "Build not found." : "The build service is unavailable."}</h1>
    <p>{state === "offline" ? "The public build API could not be reached. Try again in a moment." : state === "missing" ? "This link may have been unpublished or copied incorrectly." : "Reading the ship and fitted modules."}</p><a className="ud-main-btn" href="/">Back to EDCC</a>
  </div></section>;

  const art = shipArtFor(build.shipSlug);
  const community = build.community;
  const buildImage = ptnBuildImageSrc(community?.imageUrl) ?? art?.src;
  const galleryImages = [...new Set([community?.imageUrl, ...(community?.galleryImages ?? [])]
    .filter((image): image is string => Boolean(image))
    .map(communityImageSrc)
    .filter((image): image is string => Boolean(image)))];
  const videoEmbed = youtubeEmbedSrc(community?.videoUrl);
  const mediaItems: BuildMediaItem[] = [
    ...galleryImages.map((src, index) => ({
      kind: "image" as const,
      src,
      label: `Image ${index + 1}`,
    })),
    ...(videoEmbed ? [{ kind: "video" as const, src: videoEmbed, label: "Video" }] : []),
  ];
  const activeMedia = mediaItems[activeMediaIndex] ?? mediaItems[0];
  const buildCategory = community ? categoryForCommunityBuildRole(community.role) : null;
  const hullSize = shipSizeLabel(ship?.class);
  const buildSpecs = buildSpecSummary(build, community?.stats ?? []);
  return <section className="ud-section public-build-page"><div className="container">
    <a className="ptn-build-back" href={buildCategory ? `/builds/${buildCategory.value}/` : "/builds/"}>← Back to ship builds</a>
    {ship ? <BuildAvailabilityPanel ship={ship} modules={build.modules} /> : null}
    <div className="ptn-build-hero">
      {buildImage ? <div className="ptn-build-art"><img
        src={buildImage}
        alt={`${build.shipName} build`}
        onError={(event) => {
          event.currentTarget.hidden = true;
        }}
      /></div> : null}
      <div className="ptn-build-copy">
      <p className="eyebrow">{build.shipName}{buildCategory ? ` · ${buildCategory.label}` : ""}</p>
      {editing ? <label className="public-build-name-field">Build name<input maxLength={80} value={buildName} onChange={(event) => setBuildName(event.target.value)} /></label> : <h1>{build.shipName} <span>// {build.name}</span></h1>}
      <p>{ship?.manufacturer ? `${ship.manufacturer} build` : "EDCC community build"}</p>
      <div className="ptn-build-stats">
        {hullSize ? <span><BuildStatIcon stat={hullSize} />{hullSize}</span> : null}
        {buildSpecs.stats.map((stat, index) => <span key={`${index}-${stat}`}><BuildStatIcon stat={stat} />{stat}</span>)}
      </div>
      {buildSpecs.note ? <p className="ptn-build-calculation-note">{buildSpecs.note}</p> : null}
      {community?.description ? <div className="ptn-build-description">{communityDescription(community.description)}</div> : null}
      {community?.firegroups ? <section className="ptn-build-detail-block"><h2>Fire groups</h2><p>{community.firegroups}</p></section> : null}
      {community?.requirements ? <section className="ptn-build-detail-block"><h2>Requirements</h2><p>{community.requirements}</p></section> : null}
      {community?.limitations ? <section className="ptn-build-detail-block"><h2>Limitations</h2><p>{community.limitations}</p></section> : null}
      <p className="public-build-stamp">Shared on {new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(build.createdAt))}</p>
      <div className="public-build-actions">
        <button className="public-build-secondary-action ptn-build-copy-button" type="button" onClick={() => void copyBuildLink()}><ShareLinkIcon />Copy link</button>
        {community?.edsyUrl ? <a className="public-build-fit-link" href={community.edsyUrl} target="_blank" rel="noreferrer">EDSY <span aria-hidden="true">↗</span></a> : null}
        {community?.coriolisUrl ? <a className="public-build-fit-link" href={community.coriolisUrl} target="_blank" rel="noreferrer">Coriolis <span aria-hidden="true">↗</span></a> : null}
        {manageToken ? <button className="public-build-cancel" type="button" onClick={() => void removePublishedBuild()} disabled={saving}>Remove published build</button> : null}
        {manageToken ? <button className="ud-main-btn" type="button" onClick={() => { setEditing((value) => !value); setEditStatus(""); setEditError(""); }}>{editing ? "Cancel editing" : "Edit this build"}</button> : null}
      </div>
      {editError && !editing ? <p className="public-build-error" role="alert">{editError}</p> : null}
      {copyStatus ? <p className="public-build-status" role="status">{copyStatus}</p> : null}
      </div>
    </div>
    {community?.creatorName || community?.creatorUrl ? <p className="public-build-creator">
      Build by {community.creatorUrl
        ? <a href={community.creatorUrl} target="_blank" rel="noreferrer">{community.creatorName || "the credited creator"} <span aria-hidden="true">↗</span></a>
        : <strong>{community.creatorName}</strong>}
    </p> : null}
    {mediaItems.length ? <section className="public-build-media" aria-label="Build images and video">
      <div className="public-build-viewer">
        <div className="public-build-viewer-stage" aria-live="polite">
          {activeMedia?.kind === "image" ? <img
            src={activeMedia.src}
            alt={`${build.shipName} build ${activeMedia.label.toLowerCase()}`}
            decoding="async"
          /> : activeMedia?.kind === "video" ? <iframe
            src={activeMedia.src}
            title={`${build.shipName} build video`}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          /> : null}
        </div>
        {mediaItems.length > 1 ? <>
          <div className="public-build-viewer-controls">
            <button type="button" onClick={() => setActiveMediaIndex((index) => (index - 1 + mediaItems.length) % mediaItems.length)}>
              Previous
            </button>
            <span>{activeMedia?.label} · {activeMediaIndex + 1} of {mediaItems.length}</span>
            <button type="button" onClick={() => setActiveMediaIndex((index) => (index + 1) % mediaItems.length)}>
              Next
            </button>
          </div>
          <div className="public-build-gallery" aria-label="Choose a build image or video">
            {mediaItems.map((item, index) => <button
              type="button"
              className={index === activeMediaIndex ? "is-active" : ""}
              key={`${item.kind}-${item.src}`}
              aria-pressed={index === activeMediaIndex}
              aria-label={`Show ${item.label.toLowerCase()}`}
              onClick={() => setActiveMediaIndex(index)}
            >
              {item.kind === "image"
                ? <img src={item.src} alt="" loading="lazy" decoding="async" />
                : <span>Video</span>}
            </button>)}
          </div>
        </> : null}
      </div>
    </section> : null}
    {editing && manageToken ? <div className="public-build-editor" aria-label="Edit fitted modules">
      <p>Changes stay in this browser until you publish them. Publishing creates a new public link and retires this one.</p>
      {editError ? <p className="public-build-error" role="alert">{editError}</p> : null}
      {editStatus ? <p className="public-build-status" role="status">{editStatus}</p> : null}
      <button className="ud-main-btn" type="button" onClick={() => void saveRevision()} disabled={saving}>{saving ? "Publishing…" : "Publish revision and get new link"}</button>
    </div> : null}
    {ship ? <>
      <ShipFitWorkbench ship={ship} modules={build.modules} onChange={editing && manageToken ? modules => setBuild({ ...build, modules }) : undefined} />
    </> : <p>No verified hull data is available.</p>}
    <p className="public-build-footnote">This page shows the shared fit and published module specifications. It does not include commander identity or journal history.</p>
  </div></section>;
}

export function CommunityBuildsPage({ category }: { category?: CommunityBuildRole }) {
  const role = category ?? "";
  const [items, setItems] = useState<CommunityBuildEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const [suggestedBuildUrl, setSuggestedBuildUrl] = useState("");
  const [suggestionNotes, setSuggestionNotes] = useState("");
  const [suggestionBusy, setSuggestionBusy] = useState(false);
  const [suggestionError, setSuggestionError] = useState("");
  const [suggestionStatus, setSuggestionStatus] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!role) {
      setItems([]);
      setLoading(false);
      setLoadError("");
      return () => { cancelled = true; };
    }
    setLoading(true);
    setLoadError("");
    const query = role ? `?role=${encodeURIComponent(role)}` : "";
    fetch(`${API}/community-builds${query}`)
      .then(async (response) => {
        if (!response.ok) throw new Error(`Build recommendations could not be loaded (HTTP ${response.status}).`);
        if (!response.headers.get("content-type")?.toLowerCase().includes("application/json")) {
          throw new Error("The public build service returned an unexpected response. Check that its API is running and try again.");
        }
        return response.json() as Promise<{ items: CommunityBuildEntry[] }>;
      })
      .then((result) => { if (!cancelled) setItems(result.items); })
      .catch((error: unknown) => { if (!cancelled) setLoadError(error instanceof Error ? error.message : "Build recommendations could not be loaded."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [role]);

  const selectedCategory = categoryForCommunityBuildRole(role);
  const categoryIntroduction = selectedCategory ? ({
    hauling: "Freight fits grouped by hull size and cargo role.",
    combat: "Browse unengineered starters and specialized combat fits.",
    exploration: "From starter explorers to long-range ships prepared for deep space.",
    mining: "Laser, core, and multi-mining fits. Check each build’s tools and limpet needs.",
    "anti-xeno": "Anti-Xeno fits grouped by their combat role and engineering.",
    specialist: "Purpose-built passenger, rescue, and specialist configurations.",
  } satisfies Record<(typeof COMMUNITY_BUILD_CATEGORIES)[number]["value"], string>)[selectedCategory.value] : "";
  const groupedBuilds = useMemo(() => {
    const groups = new Map<string, CommunityBuildEntry[]>();
    for (const entry of items) {
      const label = communityBuildSection(entry);
      const group = groups.get(label);
      if (group) group.push(entry);
      else groups.set(label, [entry]);
    }
    return [...groups.entries()];
  }, [items]);
  const buildSpecsById = useMemo(
    () => new Map<string, BuildSpecSummary>(items.map((entry) => [
      entry.id,
      buildSpecSummary(entry.build, entry.stats ?? []),
    ] as const)),
    [items],
  );
  async function copyBuildLink(id: string) {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/build/${id}`);
      setCopyStatus(`${id}: Build link copied.`);
    } catch {
      setCopyStatus(`${id}: Clipboard access was unavailable.`);
    }
  }

  async function submitBuildSuggestion(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedCategory) return;
    setSuggestionBusy(true);
    setSuggestionError("");
    setSuggestionStatus("");
    try {
      let url: URL;
      try {
        url = new URL(suggestedBuildUrl.trim());
      } catch {
        throw new Error("Enter the full EDCC public build URL.");
      }
      if (!["http:", "https:"].includes(url.protocol) || url.username || url.password || url.hostname !== window.location.hostname) {
        throw new Error("Use a public build link on this EDCC website.");
      }
      const buildId = /^\/build\/([A-Za-z0-9_-]{16})\/?$/.exec(url.pathname)?.[1];
      if (!buildId) throw new Error("The link must point to an EDCC public build.");
      const response = await fetch(`${API}/community-builds`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ buildId, role: selectedCategory.value, ...(suggestionNotes.trim() ? { notes: suggestionNotes.trim() } : {}) }),
      });
      const result = await response.json() as { error?: { message?: string }; submission?: unknown };
      if (!response.ok) throw new Error(result.error?.message ?? `The build could not be submitted (HTTP ${response.status}).`);
      setSuggestionStatus("Build sent to the review queue. It will appear here only after approval.");
      setSuggestedBuildUrl("");
      setSuggestionNotes("");
    } catch (error) {
      setSuggestionError(error instanceof Error ? error.message : "The build could not be submitted. Try again.");
    } finally {
      setSuggestionBusy(false);
    }
  }

  return <section className={`page-width reference-page community-builds-page${selectedCategory ? " category-detail-page" : ""}`}>
    <header className="community-build-library-hero">
      <img src="/ship-builds-bg.webp" alt="" aria-hidden="true" />
      <h1>{selectedCategory ? `${selectedCategory.label} ship builds` : "Community ship builds"}</h1>
    </header>
    {selectedCategory ? <a className="community-build-back" href="/builds/">← All build categories</a> : null}
    <div className="community-build-actions">
      <a href="/builds/new/">Build a ship</a>
    </div>
    {selectedCategory ? <section className="community-build-overview" aria-label={`${selectedCategory.label} build overview`}>
      <h2>{selectedCategory.label} builds</h2>
      <p>{categoryIntroduction}</p>
    </section> : null}
    {!selectedCategory ? <section className="community-build-categories" aria-labelledby="community-build-categories-title">
      <div className="community-build-categories-head">
        <h2 id="community-build-categories-title" className="visually-hidden">Build categories</h2>
      <p>Browse builds by mission.</p>
      </div>
      <nav className="community-build-category-grid" aria-label="Ship-build categories">
      {COMMUNITY_BUILD_CATEGORIES.map((buildCategory) => <a
        className="community-build-category"
        key={buildCategory.value}
        href={`/builds/${buildCategory.value}/`}
      >
        <img src={buildCategory.image} alt="" aria-hidden="true" loading="lazy" />
        <span>{buildCategory.label}</span>
      </a>)}
      </nav>
    </section> : null}
    {selectedCategory && loading ? <p className="empty-copy" role="status">Loading builds…</p> : null}
    {selectedCategory && loadError ? <p className="community-build-error" role="alert">{loadError}</p> : null}
    {selectedCategory && !loading && !loadError && items.length === 0 ? <p className="empty-copy">No builds in this category yet.</p> : null}
    {selectedCategory ? <div className="community-build-groups">
      {groupedBuilds.map(([groupName, entries]) => <section className="community-build-section" key={groupName}>
        {selectedCategory.value === "mining" ? <div className="limpet-warning" role="note">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10.3 4.3 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" /><path d="M12 9v5m0 3h.01" /></svg>
          <strong>DON’T FORGET YOUR LIMPETS!</strong>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10.3 4.3 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" /><path d="M12 9v5m0 3h.01" /></svg>
        </div> : null}
        <header className="community-build-section-heading">
          <h2>{groupName}</h2>
          <p>{communityGroupSummary(selectedCategory.value, groupName)} <span>{entries.length} {entries.length === 1 ? "build" : "builds"}</span></p>
        </header>
        <div className="community-build-list">{entries.map((entry) => {
          const art = shipArtFor(entry.build.shipSlug);
          const buildImage = ptnBuildImageSrc(entry.imageUrl)
            ?? communityImageSrc(entry.imageUrl ?? entry.galleryImages?.[0] ?? "")
            ?? art?.src;
          const buildSpecs = buildSpecsById.get(entry.id);
          return <article className={`community-build-listing-item${buildImage ? "" : " community-build-item-no-art"}`} key={entry.id}>
            <h3 className="community-build-listing-title"><a href={`/build/${entry.build.id}`}>{entry.build.shipName} <span>// {entry.build.name}</span></a></h3>
            {buildImage ? <img
              src={buildImage}
              alt={`${entry.build.shipName} build`}
              loading="lazy"
              onError={(event) => {
                if (art?.src && event.currentTarget.src !== new URL(art.src, window.location.href).href) {
                  event.currentTarget.src = art.src;
                  return;
                }
                event.currentTarget.hidden = true;
                event.currentTarget.closest("article")?.classList.add("community-build-item-no-art");
              }}
            /> : null}
            <div className="community-build-listing-copy">
              <ul className="community-build-stats" aria-label="Build specifications">
                {buildSpecs?.stats.map((stat, index) => <li key={`${index}-${stat}`}><BuildStatIcon stat={stat} /><span>{stat}</span></li>)}
              </ul>
              {buildSpecs?.note ? <p className="community-build-calc-note">{buildSpecs.note}</p> : null}
              {entry.description ? <div className="community-build-listing-description">{communityDescription(entry.description)}</div> : null}
              {entry.firegroups ? <p className="community-build-listing-note"><strong>Fire groups:</strong> {entry.firegroups}</p> : null}
              {entry.requirements ? <p className="community-build-listing-note"><strong>Requirements:</strong> {entry.requirements}</p> : null}
              {entry.limitations ? <p className="community-build-listing-note"><strong>Limitations:</strong> {entry.limitations}</p> : null}
              {copyStatus.startsWith(`${entry.build.id}:`) ? <p className="community-build-copy-status" role="status">{copyStatus.slice(entry.build.id.length + 2)}</p> : null}
            </div>
            <div className="community-build-listing-actions">
              <button type="button" className="community-build-copy-link" onClick={() => void copyBuildLink(entry.build.id)} aria-label={`Copy link to ${entry.build.name}`}>
                <ShareLinkIcon />
                <span>Copy Link</span>
              </button>
              <div className="community-build-fit-links">
                {entry.edsyUrl ? <a href={entry.edsyUrl} target="_blank" rel="noreferrer">EDSY</a> : null}
                {entry.coriolisUrl ? <a href={entry.coriolisUrl} target="_blank" rel="noreferrer">Coriolis</a> : null}
                <a href={`/build/${entry.build.id}`}>Build details</a>
              </div>
            </div>
          </article>;
        })}</div>
      </section>)}
    </div> : null}
    {selectedCategory ? <section className="community-build-suggest" aria-labelledby="community-build-suggest-title">
      <div>
        <h2 id="community-build-suggest-title">Suggest a build</h2>
        <p>Share a public EDCC build for this category. Submissions stay private until reviewed.</p>
      </div>
      <form onSubmit={(event) => void submitBuildSuggestion(event)}>
        <label>Public EDCC build link<input type="url" required value={suggestedBuildUrl} onChange={(event) => setSuggestedBuildUrl(event.target.value)} placeholder={`${window.location.origin}/build/…`} /></label>
        <label>Reviewer note <span>Optional</span><textarea value={suggestionNotes} onChange={(event) => setSuggestionNotes(event.target.value)} maxLength={400} rows={3} placeholder="What should reviewers verify?" /></label>
        <button type="submit" disabled={suggestionBusy}>{suggestionBusy ? "Sending…" : "Send for review"}<span aria-hidden="true">→</span></button>
        {suggestionError ? <p role="alert">{suggestionError}</p> : null}
        {suggestionStatus ? <p role="status">{suggestionStatus}</p> : null}
      </form>
    </section> : null}
  </section>;
}
